import { createApiClient } from '../../../../../api/apiClient';
import { buildOrderRulesRequest, unwrapApiPayload } from './requestBuilder';
import { OrderRulesDashboardResponse, OrderRulesFilterParams, OrderRulesRecommendationsResponse } from './types';

// ─── API: /api/order-rules/recommendations ───────────────────────────────────

export const fetchOrderRulesRecommendations = async (
  params: OrderRulesFilterParams,
  locationIds: string[],
  pagination: { page: number; size: number },
): Promise<OrderRulesRecommendationsResponse> => {
  const api = createApiClient();
  const body = buildOrderRulesRequest(params, locationIds, pagination);
  const res = await api.post<OrderRulesRecommendationsResponse>('/order-rules/recommendations', body);
  return unwrapApiPayload<OrderRulesRecommendationsResponse>(res.data);
};

// ─── Normalizer: backend → legacy dashboard shape ────────────────────────────

export const normalizeRecommendationsResponse = (
  data: OrderRulesRecommendationsResponse,
): Pick<OrderRulesDashboardResponse, 'recommendations' | 'openRecommendations' | 'recommendationWindowStart' | 'recommendationWindowEnd'> => ({
  // Backend field names (materialNo, customerName, recommend, *PerWeek, *AtLeastLayer) differ from the UI shape.
  recommendations: (data.skuRecommendations ?? []).map((row: any) => ({
    region: row.region,
    market: row.market,
    location: row.location,
    customerBp: row.customerBp,
    customer: row.customer ?? row.customerName,
    materialNumber: row.materialNumber ?? row.materialNo,
    packageName: row.packageName,
    description: row.description ?? row.materialDescription,
    skuEligible: row.skuEligible,
    recommendation: row.recommendation ?? row.recommend,
    customerEnrolled: row.customerEnrolled,
    confidence: row.confidence,
    deliveryDaysPerWeek: Number(row.deliveryDaysPerWeek ?? 0),
    casesPerLayer: Number(row.casesPerLayer ?? 0),
    pyPlus6AvgCases: Number(row.pyPlus6AvgCases ?? row.pyPlus6AvgCasesPerWeek ?? 0),
    pyMinus6AvgCases: Number(row.pyMinus6AvgCases ?? row.pyMinus6AvgCasesPerWeek ?? 0),
    cyMinus6AvgCases: Number(row.cyMinus6AvgCases ?? row.cyMinus6AvgCasesPerWeek ?? 0),
    pyPlus6GteLayer: row.pyPlus6GteLayer ?? row.pyPlus6AvgAtLeastLayer,
    pyMinus6GteLayer: row.pyMinus6GteLayer ?? row.pyMinus6AvgAtLeastLayer,
    cyMinus6GteLayer: row.cyMinus6GteLayer ?? row.cyMinus6AvgAtLeastLayer,
    nextAction: row.nextAction,
    potentialCases: Number(row.potentialCases ?? 0),
  })),
  openRecommendations: (data.openRecommendations ?? (data as any).openRecommendationsByCustomer ?? []).map((row: any) => ({
    region: row.region,
    market: row.market,
    location: row.location,
    customerBp: row.customerBp,
    customer: row.customer ?? row.customerName,
    high: Number(row.high ?? 0),
    medium: Number(row.medium ?? 0),
    low: Number(row.low ?? 0),
  })),
  recommendationWindowStart: data.recommendationWindowStart,
  recommendationWindowEnd: data.recommendationWindowEnd,
});
